import React, { useState } from 'react';
import CommonContainer from '../../../common/CommonContainer';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import withStyles from '@material-ui/core/styles/withStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import { clearZReports, downloadZReport, fetchZReports } from '../../../../actions/report-actions';
import SearchZReportsForm from './SearchZReportsForm';
import LoadingSnackbar from '../../../common/LoadingSnackbar';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import { isNumber } from '../../../../utils/report-utils';
import DataGridCell from '../../../common/table/DataGridCell';
import PropTypes from 'prop-types';
import {
  MACHINE_ID_COLUMN,
  CASH_REGISTER_COLUMN,
  ID_COLUMN,
  NUMBER_COLUMN,
  DATE_TIME_COLUMN,
  CREATED_BY_COLUMN_2, SIE_EXPORT_COLUMN,
} from '../standard/StandardReportConsts';
import { getHeaderTranslationForStandardReports } from '../standard/StandardReportUtils';
import { DefaultToolbar } from '../standard/StandardReportCustom';
import NoDataAvailableWithIcon from '../../../common/charts/NoDataAvailableWithIcon';

const styles = theme => ({
  reportHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  infoText: {
    maxWidth: theme.breakpoints.values.sm * 2,
    margin: theme.spacing(2),
  },
  pagination: {
    margin: `${theme.spacing(1.5)}px 0`,
  },
  tableWrapper: {
    margin: '12px 0',
    [theme.breakpoints.down('sm')]: {
      margin: '12px auto',
    },
  },
});

const COLUMN_WIDTH = 240;

const ZReports = ({ machines, zReports, clearZReports, fetchZReports, downloadZReport, classes }) => {
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const { formatMessage } = useIntl();
  const printRef = React.useRef();
  
  React.useEffect(() => {
    clearZReports();
  }, [clearZReports]);
  
  const availableHeaders = [CASH_REGISTER_COLUMN, DATE_TIME_COLUMN, NUMBER_COLUMN, SIE_EXPORT_COLUMN, CREATED_BY_COLUMN_2];
  const availableMachines = machines.map(({ Id, Name }) => ({ label: Name, value: Id }));
  const columnOrder = (a, b) => availableHeaders.indexOf(a) - availableHeaders.indexOf(b);
  const shouldHideCashRegister = () => selectedMachines[0] !== null && selectedMachines.length === 1;
  const hiddenHeaders = [shouldHideCashRegister() && CASH_REGISTER_COLUMN, ID_COLUMN, MACHINE_ID_COLUMN].filter(Boolean);
  const zReportHeaders = zReports?.length > 0 && [...Object.keys(zReports[0]), SIE_EXPORT_COLUMN, CASH_REGISTER_COLUMN]
    .sort(columnOrder)
    .filter(header => !hiddenHeaders.includes(header));
  
  const downloadZReportDetails = {
    machines: machines,
  };
  
  const getDataForReports = (reports) => {
    return reports.map((report, index) => {
      return {
        ...report,
        id: index,
        CashRegister: availableMachines.find(option => option.value === report.machineID)?.label || 'Unknown',
        'SIE-Export': report.Number,
      };
    });
  };
  
  const replaceNumberName = (header) => {
    return header === 'Number'
      ? formatMessage({ id: 'report.zreport.number', defaultMessage: 'Z-report nr.' })
      : header;
  };
  
  return (
    <CommonContainer ref={printRef} width={'auto'} hAlign="center">
      
      <LoadingSnackbar
        downloadFile={true}
        progress={downloadProgress}
        label={<FormattedMessage id="loading" defaultMessage="Loading" />}
      />
      
      <h1 className={classes.reportHeader}>
        <FormattedMessage
          id="zreports.title"
          defaultMessage="Z-Reports"
        />
      </h1>
      
      <div className={classes.infoText}>
        <FormattedMessage
          id="zreports.info"
          defaultMessage="
          Here you can make a copy of the z-reports that have previously been generated at checkout. First choose which cashier you are interested in.
           Then select a date range and click ”Search”. A list of generated z-reports is displayed on the screen.
          Select the z-report you are interested in (click icon next to z-report number). The report is displayed as a pdf file in A4 format."
        />
      </div>
      
      <SearchZReportsForm
        fetchZReports={fetchZReports}
        machinesOptions={availableMachines}
        setSelectedMachines={setSelectedMachines}
      />
      
      {zReportHeaders?.length > 0 &&
      <div className={classes.tableWrapper}>
        <RegularDataGrid
          reportName={'ZReport'}
          setDownloadProgress={setDownloadProgress}
          columns={
            zReportHeaders.map(header => ({
              headerName: getHeaderTranslationForStandardReports(formatMessage, replaceNumberName(header)),
              field: header,
              headerAlign: 'center',
              align: 'center',
              width: COLUMN_WIDTH,
              hide: hiddenHeaders.includes(header),
              valueGetter: (params) => isNumber(params.value) ? Number(params.value) : params.value,
              renderCell: (params) =>
                <DataGridCell
                  header={header}
                  value={params.value}
                  LogRecordId={params.row.Id}
                  machineId={params.row.machineID}
                  setDownloadProgress={setDownloadProgress}
                  downloadZReport={downloadZReport}
                  machinesOptions={availableMachines}
                  downloadZReportDetails={downloadZReportDetails}
                />,
            }))}
          rows={getDataForReports(zReports)}
          components={{
            Toolbar: () => DefaultToolbar(printRef),
          }}
          componentProps={{ printRef: printRef }}
        />
      </div>}
  
      {!!zReports && zReports.length === 0 &&
      <CommonContainer className={classes.noDataFound}>
        <NoDataAvailableWithIcon />
      </CommonContainer>
      }
    </CommonContainer>
  );
};

ZReports.propTypes = {
  machines: PropTypes.array,
  zReports: PropTypes.array,
  clearZReports: PropTypes.func,
  fetchZReports: PropTypes.func,
  downloadZReport: PropTypes.func,
  classes: PropTypes.object,
};

const decorate = compose(
  withStyles(styles),
  connect(
    store => ({
      machines: store.user.machines,
      zReports: store.report.zReports,
    }),
    dispatch => bindActionCreators({ fetchZReports, downloadZReport, clearZReports }, dispatch)),
);

export default decorate(ZReports);